import React from 'react';

// material components
import { Toolbar } from '@material-ui/core';
import { IconButton, Tooltip, Typography } from '@material-ui/core';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import KeyboardBackspaceIcon from '@material-ui/icons/KeyboardBackspace';
// custom components

// constants

// import styling
import '../global/TableToolbar.css';
import PropTypes from 'prop-types';
import ZoomOutIcon from '@material-ui/icons/ZoomOut';
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import { Popper } from '@mui/material';
import withShipment from '../../withShipment';
import { userInfoSelector, viewOnlySelector } from '../../redux/selectors/auth';
import { filterToolbarItems } from '../../constants/readOnlyAccess';
import Utils from '../Utils/Utils';
import scrollAppToTop from '../Utils/scrollToTop';

class CustomToolbar extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      anchorEl: null,
    };
  }

  componentDidMount() {
    // Opening a page (or a sub-view that mounts its own toolbar) starts at the
    // top of the page, not wherever the previous view was scrolled to.
    scrollAppToTop();
  }

  componentDidUpdate(
    prevProps: Readonly<P>,
    prevState: Readonly<S>,
    snapshot: SS
  ) {
    if (prevProps.headerButtons !== this.props.headerButtons) {
      this.setState({ anchorEl: null });
    }
    // The other half of the app's page headers (TopToolbar is the first). A
    // page that swaps a sub-view in place without changing the route re-titles
    // its toolbar, so the title is the global signal for "new content under
    // this header" — see the note in TopToolbar. Titles are static per view,
    // so a data refresh can't trigger this.
    if (prevProps.title !== this.props.title) {
      scrollAppToTop();
    }
  }

  getToolbarButtons = () => {
    switch (this.props.title) {
      default:
        return null;
    }
  };

  getCreateOrBackButtons = () => {
    if (this.props.creatingOrder) {
      return (
        <div className="contextualMenu">
          <Tooltip title={this.props.backTitle}>
            <IconButton
              aria-label={this.props.backTitle}
              onClick={() => {
                this.props.handleClose();
              }}
            >
              <KeyboardBackspaceIcon />
            </IconButton>
          </Tooltip>
        </div>
      );
    } else {
      // The generic "create" affordance. Removed outright at a VIEW-ONLY
      // location rather than shown disabled — offering a door into an add
      // form that can never be saved is worse than no door.
      if (this.props.viewOnly) return null;
      return (
        <div className="contextualMenu">
          <Tooltip title={this.props.createTitle}>
            <IconButton
              aria-label={this.props.createTitle}
              onClick={this.props.onClick}
            >
              <AddCircleIcon />
            </IconButton>
          </Tooltip>
        </div>
      );
    }
  };

  handleClick = (event, button) => {
    if (button.popper) this.setState({ anchorEl: event.currentTarget });
    else button.handler();
  };

  handleClosePopper = () => {
    this.setState({ anchorEl: null });
  };

  render() {
    const popperOpen = Boolean(this.state.anchorEl);
    // Central read-only enforcement for every page using this toolbar:
    // write actions are removed, read actions (Back, Download PDF, Zoom,
    // View Report...) are kept. See constants/readOnlyAccess.js.
    const headerButtons = filterToolbarItems(
      this.props.headerButtons,
      this.props.viewOnly
    );
    const rightHeaderButtons = filterToolbarItems(
      this.props.rightHeaderButtons,
      this.props.viewOnly
    );
    return (
      <div
        className="toolbar"
        style={{
          backgroundColor: Utils.isValidColor(
            this.props.userInfo?.employeeColor
          )
            ? this.props.userInfo?.employeeColor
            : '#7896ff',
        }}
      >
        <Toolbar>
          {this.props.customIcons ? (
            this.props.customIcons
          ) : headerButtons ? (
            <div className="contextualMenu">
              {headerButtons.map((button, index) =>
                button.divider ? (
                  <React.Fragment key={`divider-${index}`}>
                    {button.divider}
                  </React.Fragment>
                ) : (
                  <div key={button.title + index}>
                    <Tooltip title={button.title}>
                      <IconButton
                        aria-label={button.title}
                        onClick={(event) => this.handleClick(event, button)}
                        disabled={button.disabled}
                      >
                        {button.icon}
                      </IconButton>
                    </Tooltip>
                    <Popper
                      open={popperOpen}
                      anchorEl={this.state.anchorEl}
                      placement="bottom-start"
                      style={{ zIndex: '100' }}
                    >
                      {button.popper &&
                        React.cloneElement(button.popper, {
                          handleClosePopper: this.handleClosePopper,
                        })}
                    </Popper>
                  </div>
                )
              )}
              {this.props.scale && this.props.addZoom && (
                <div className="zoom-box">
                  <div style={{ padding: '5px', cursor: 'pointer' }}>
                    <IconButton
                      aria-label="Zoom Out"
                      onClick={this.props.zoomOut}
                      disabled={this.props.scale === 1}
                    >
                      <ZoomOutIcon />
                    </IconButton>
                  </div>
                  <span style={{ padding: '5px' }}>
                    {this.props.scale * 100}%
                  </span>
                  <div style={{ padding: '5px', cursor: 'pointer' }}>
                    <IconButton
                      aria-label="Zoom In"
                      onClick={this.props.zoomIn}
                      disabled={this.props.scale === 2}
                    >
                      <ZoomInIcon />
                    </IconButton>
                  </div>
                </div>
              )}
            </div>
          ) : this.props.title === 'test' ? (
            this.getToolbarButtons()
          ) : (
            this.getCreateOrBackButtons()
          )}

          <div
            style={{
              flex: '1 1 auto',
              minWidth: 0,
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'flex-end',
              overflow: 'hidden',
            }}
          >
            <Typography
              className="titleTypography"
              variant="h6"
              id="tableTitle"
            >
              {this.props.title}
            </Typography>
            {rightHeaderButtons && (
              <div style={{ display: 'flex' }}>
                {rightHeaderButtons.map((button, index) => (
                  <Tooltip title={button.title} key={button.title + index}>
                    <IconButton
                      aria-label={button.title}
                      onClick={button.handler}
                      disabled={button.disabled}
                    >
                      {button.icon}
                    </IconButton>
                  </Tooltip>
                ))}
              </div>
            )}
          </div>
        </Toolbar>
      </div>
    );
  }
}

CustomToolbar.propTypes = {
  title: PropTypes.string,
  createTitle: PropTypes.string,
  onClick: PropTypes.func,
  creatingOrder: PropTypes.bool,
  handleClose: PropTypes.func,
};

CustomToolbar.defaultProps = {
  title: '',
  createTitle: '',
  onClick: () => {},
  creatingOrder: false,
  handleClose: () => {},
};

const mapStateToProps = (state) => ({
  userInfo: userInfoSelector(state),
  viewOnly: viewOnlySelector(state),
});

export default withShipment(
  {
    mapStateToProps,
  },
  CustomToolbar
);
